import { Mail, MapPin, Clock, ShieldCheck } from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";
import EnquiryForm from "../components/EnquiryForm";

const INFO = [
  { icon: Mail, color: "#06B6D4", title: "Email", lines: ["yasin@nationalclinicalservices.co.uk", "Every enquiry answered by our clinical services team"] },
  { icon: MapPin, color: "#F59E0B", title: "Coverage", lines: ["United Kingdom", "Supporting PCNs, GP practices, federations, ICBs and NHS organisations"] },
  { icon: Clock, color: "#EF4444", title: "Response", lines: ["Prompt, personal responses", "Complex proposals scoped with you directly"] },
];

const Contact = () => (
  <main data-testid="contact-page">
      <Seo
        title="Contact Our Clinical Services Team | NCS"
        description="Commission a clinical service, request pharmacists or pharmacy technicians, or both — one short enquiry, no account needed."
      />
    <section className="relative overflow-hidden pt-32 sm:pt-40 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Contact us</p>
        <h1
          data-testid="contact-heading"
          className="mt-4 font-display font-extrabold tracking-tight leading-[1.04] text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
        >
          <MaskedLine delay={0.15}>Speak to our clinical</MaskedLine>
          <MaskedLine delay={0.3}><span className="font-serif-accent">services</span> team.</MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <div
            data-testid="response-guarantee-banner"
            className="mt-8 inline-flex flex-wrap items-center gap-3 bg-white border border-[#E2E8F0] rounded-2xl px-6 py-4 shadow-sm"
          >
            <ShieldCheck size={20} className="text-[#10B981]" />
            <p className="text-sm font-semibold text-[#0B192C]">
              One form for everything — commission services, request workforce, or combine both in a single enquiry.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="pb-16 sm:pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <Reveal className="lg:col-span-7">
          <EnquiryForm journey="general" heading="Send us an enquiry" />
        </Reveal>
        <div className="lg:col-span-5 space-y-5">
          {INFO.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div
                data-testid={`contact-info-${c.title.toLowerCase()}`}
                className="flex gap-5 bg-white border border-[#E2E8F0] rounded-2xl p-6 transition-shadow duration-500 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]"
              >
                <span
                  className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${c.color}1A`, color: c.color }}
                >
                  <c.icon size={20} />
                </span>
                <div>
                  <h3 className="font-display font-bold text-[#0B192C]">{c.title}</h3>
                  <p className="text-sm font-semibold text-[#0B192C] mt-1 break-all">{c.lines[0]}</p>
                  <p className="text-sm text-[#64748B]">{c.lines[1]}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <div className="bg-[#0B192C] rounded-2xl p-7" data-testid="contact-journeys-card">
              <h3 className="font-display font-bold text-white">Two ways to work with NCS</h3>
              <ul className="mt-4 space-y-3 text-sm text-white/65">
                <li className="flex gap-2.5">
                  <span className="w-2 h-2 rounded-full bg-[#10B981] mt-1.5 shrink-0" />
                  Commission a defined clinical service or project, delivered by NCS.
                </li>
                <li className="flex gap-2.5">
                  <span className="w-2 h-2 rounded-full bg-[#06B6D4] mt-1.5 shrink-0" />
                  Request pharmacists and pharmacy technicians to work within your organisation.
                </li>
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </main>
);

export default Contact;
